import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingCart, Star, Eye, Plus } from 'lucide-react';
import { motion } from 'framer-motion';
import { Product } from '@/services/types/domain';
import { useCart } from '@/store/cartStore';
import { useAuth } from '@/store/authStore';
import Button from '@/components/ui/Button';
import Image from '@/components/ui/Image';
import { formatPrice, calculateDiscountPercentage, UPLOADS_URL } from '@/utils/utils';
import { placeholders } from '@/utils/placeholders';
import { toast } from 'react-hot-toast';
import { cn } from '@/utils/utils';
import wishlistApi from '@/services/api/wishlist';

interface ProductCardProps {
    product: Product;
    className?: string;
    showQuickAdd?: boolean;
    showWishlist?: boolean;
    initialWishlisted?: boolean;
    imageSize?: 'sm' | 'md' | 'lg';
    onAddToCart?: (productId: number) => void; // Nueva prop opcional
}

const ProductCard: React.FC<ProductCardProps> = ({
    product,
    className,
    showQuickAdd = true,
    showWishlist = true,
    initialWishlisted = false,
    imageSize = 'md',
    onAddToCart, // Nueva prop
}) => {
    const { addProduct } = useCart();
    const { isAuthenticated } = useAuth();
    const [isHovered, setIsHovered] = useState(false);
    const [isAddingToCart, setIsAddingToCart] = useState(false);

    // Obtener imagen principal - con validación de seguridad
    const images = product.images || [];
    const primaryImage =
        images.length > 0
            ? images.find(img => img.is_primary) || images[0]
            : { image_url: product.primary_image, alt_text: product.name };
    /*if (product.id == 21)
        console.log('ProductCard: Invalid product data', images, primaryImage);*/
    const secondaryImage = images.find(img => !img.is_primary && img !== primaryImage);
    /*if (product.id == 21) {
        console.log('ProductCard: Invalid product data2', images, primaryImage);
    }*/
    // Calcular descuento si hay precio de comparación - con validación
    const hasDiscount = product.compare_price && product.compare_price > product.price;
    const discountPercentage = hasDiscount
        ? calculateDiscountPercentage(product.compare_price!, product.price)
        : 0;

    // Validar que el producto tenga las propiedades necesarias
    if (!product || !product.id) {
        console.warn('ProductCard: Invalid product data', product);
        return null;
    }

    // Determinar tamaño de imagen
    const imageSizes = {
        sm: 'h-40 sm:h-48',
        md: 'h-48 sm:h-56 lg:h-64',
        lg: 'h-64 sm:h-72 lg:h-80',
    };

    /*const handleAddToCartAnterior = async () => {
        if (!isAuthenticated) {
            toast.error('Debes iniciar sesión para agregar productos al carrito');
            return;
        }
        try {
            setIsAddingToCart(true);
            await addProduct(product.id, 1);
        } catch (error) {
            // El error ya se maneja en el store
        } finally {
            setIsAddingToCart(false);
        }
    };*/
    const handleAddToCart = async () => {
        if (!isAuthenticated) {
            toast.error('Debes iniciar sesión para agregar productos al carrito');
            return;
        }
        try {
            setIsAddingToCart(true);
            // Si existe la prop onAddToCart, la usamos
            if (onAddToCart) {
                await onAddToCart(product.id);
            } else {
                // Si no, usamos la implementación por defecto
                await addProduct(product.id, 1);
            }

        } catch (error) {
            // El error ya se maneja en el store
        } finally {
            setIsAddingToCart(false);
        }
    };
    const [wishlisted, setWishlisted] = useState<boolean>(!!initialWishlisted);
    const [wlBusy, setWlBusy] = useState(false);
    const handleWishlistToggle = async (e: React.MouseEvent) => {
        // Evita que el click navegue si el Card está dentro de un <Link>
        e.preventDefault();
        e.stopPropagation();

        /*if (!isAuthenticated) {
            // Llévalo a login con retorno a la página actual
            navigate("/login", { state: { from: location.pathname + location.search } });
            return;
        }*/

        if (wlBusy) return;
        setWlBusy(true);

        const prev = wishlisted;
        try {
            if (!wishlisted) {
                // Optimista: marca como añadido
                setWishlisted(true);
                const r = await wishlistApi.add(product.id); // maneja 409 internamente
                if (r.added) {
                    toast.success("Agregado a tu lista de deseos");
                } else if (r.exists) {
                    toast("Este producto ya estaba en tu lista 🧡");
                }
                //onWishlistChange?.(true);
            } else {
                // Quitar
                setWishlisted(false);
                await wishlistApi.remove(product.id);
                toast("Quitado de tus deseos");
                //onWishlistChange?.(false);
            }
        } catch (err: any) {
            // Rollback
            setWishlisted(prev);
            toast.error(err?.message || "No se pudo actualizar la lista");
        } finally {
            setWlBusy(false);
        }
    };

    return (
        <motion.div
            className={cn(
                'bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden group',
                'hover:shadow-md transition-all duration-300',
                className
            )}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            whileHover={{ y: -4 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
        >
            {/* Imagen del producto */}
            <div className={cn('relative overflow-hidden', imageSizes[imageSize])}>
                <Link to={`/product/${product.slug}`} className="block w-full h-full">
                    {/* Imagen principal */}
                    <Image
                        src={UPLOADS_URL + primaryImage?.image_url}
                        alt={primaryImage?.alt_text || product.name}
                        fallback={placeholders.product}
                        className={cn(
                            'w-full h-full object-cover transition-transform duration-300',
                            isHovered && secondaryImage ? 'scale-110' : 'scale-100'
                        )}
                        containerClassName="relative w-full h-full"
                    />

                    {/* Imagen secundaria en hover */}
                    {secondaryImage && (
                        <Image
                            src={secondaryImage.image_url}
                            alt={secondaryImage.alt_text || product.name}
                            fallback={placeholders.product}
                            className={cn(
                                'absolute inset-0 w-full h-full object-cover transition-opacity duration-300',
                                isHovered ? 'opacity-100' : 'opacity-0'
                            )}
                            showPlaceholder={false}
                        />
                    )}
                </Link>

                {/* Badges */}
                <div className="absolute top-2 left-2 flex flex-col gap-1">
                    {hasDiscount && (
                        <span className="bg-red-500 text-white text-xs font-medium px-2 py-1 rounded">
                            -{discountPercentage}%
                        </span>
                    )}
                    {product.is_featured && (
                        <span className="bg-primary-500 text-white text-xs font-medium px-2 py-1 rounded">
                            Destacado
                        </span>
                    )}
                    {(product.stock_quantity || 0) === 0 && (
                        <span className="bg-gray-500 text-white text-xs font-medium px-2 py-1 rounded">
                            Sin stock
                        </span>
                    )}
                </div>

                {/* Botones de acción */}
                <div className={cn(
                    'absolute top-2 right-2 flex flex-col gap-2 transition-opacity duration-300',
                    isHovered ? 'opacity-100' : 'opacity-0'
                )}>
                    {/* {showWishlist && (
                        <button
                            onClick={handleWishlistToggle}
                            className="w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors"
                        >
                            <Heart className="w-4 h-4 text-gray-600" />
                        </button>
                    )} */}
                    {showWishlist && (
                        <button
                            onClick={handleWishlistToggle}
                            aria-pressed={wishlisted}
                            aria-label={wishlisted ? "Quitar de favoritos" : "Agregar a favoritos"}
                            title={wishlisted ? "Quitar de favoritos" : "Agregar a favoritos"}
                            disabled={wlBusy}
                            className={[
                                "w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors",
                                wishlisted ? "text-red-600 ring-red-200" : "text-gray-600 ring-gray-200",
                                wlBusy ? "opacity-70 cursor-not-allowed" : "cursor-pointer",
                            ].join(" ")}
                        >
                            {/* Lucide permite fill; usamos currentColor cuando está wishlisted */}
                            <Heart className="w-4 h-4" fill={wishlisted ? "currentColor" : "none"} />
                        </button>
                    )}

                    <Link
                        to={`/product/${product.slug}`}
                        className="w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors"
                    >
                        <Eye className="w-4 h-4 text-gray-600" />
                    </Link>
                </div>

                {/* Botón de agregar al carrito */}
                {showQuickAdd && (product.stock_quantity || 0) > 0 && (
                    <div className={cn(
                        'absolute bottom-2 left-2 right-2 transition-opacity duration-300',
                        isHovered ? 'opacity-100' : 'opacity-0'
                    )}>
                        <Button
                            onClick={handleAddToCart}
                            loading={isAddingToCart}
                            size="sm"
                            fullWidth
                            leftIcon={<ShoppingCart className="w-4 h-4" />}
                        >
                            Agregar
                        </Button>
                    </div>
                )}
            </div>

            {/* Información del producto */}
            <div className="p-3 sm:p-4">
                {/* Categoría */}
                {product.category && (
                    <Link
                        to={`/category/${product.category.id || 1}`}
                        className="text-xs text-gray-500 hover:text-primary-600 transition-colors"
                    >
                        {product.category.name}
                    </Link>
                )}

                {/* Nombre del producto */}
                <Link to={`/product/${product.slug}`}>
                    <h3 className="font-medium text-gray-900 mt-1 mb-2 line-clamp-2 hover:text-primary-600 transition-colors text-sm sm:text-base">
                        {product.name || 'Producto sin nombre'}
                    </h3>
                </Link>

                {/* Rating y reseñas */}
                {product.average_rating && product.reviews_count && product.reviews_count > 0 && (
                    <div className="flex items-center gap-1 mb-2">
                        <div className="flex items-center">
                            {[...Array(5)].map((_, i) => (
                                <Star
                                    key={i}
                                    className={cn(
                                        'w-3 h-3',
                                        i < Math.floor(product.average_rating!)
                                            ? 'text-yellow-400 fill-current'
                                            : 'text-gray-300'
                                    )}
                                />
                            ))}
                        </div>
                        <span className="text-xs text-gray-500">
                            ({product.reviews_count})
                        </span>
                    </div>
                )}

                {/* Precio */}
                <div className="flex items-center gap-2 mb-2">
                    <span className="text-base sm:text-lg font-bold text-gray-900">
                        {formatPrice(product.price || 0)}
                    </span>
                    {hasDiscount && (
                        <span className="text-xs sm:text-sm text-gray-500 line-through">
                            {formatPrice(product.compare_price!)}
                        </span>
                    )}
                </div>

                {/* Stock */}
                <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                        {(product.stock_quantity || 0) > 0
                            ? `${product.stock_quantity} disponibles`
                            : 'Sin stock'
                        }
                    </span>

                    {product.has_variants && (
                        <span className="text-xs text-primary-600 font-medium">
                            Variantes
                        </span>
                    )}
                </div>

                {/* Botón de agregar al carrito (móvil) */}
                {showQuickAdd && (product.stock_quantity || 0) > 0 && (
                    <div className="mt-3">
                        <Button
                            onClick={handleAddToCart}
                            loading={isAddingToCart}
                            size="sm"
                            fullWidth
                            leftIcon={<Plus className="w-4 h-4" />}
                            className="text-xs sm:text-sm"
                        >
                            <span className="hidden xs:inline">Agregar al carrito</span>
                            <span className="xs:hidden">Agregar</span>
                        </Button>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default ProductCard;